import { getRouteApi, Outlet } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Cancel from '@/assets/icons/cancel.svg?react'
import HistoryIcon from '@/assets/icons/history.svg?react'
import SendLink from '@/assets/icons/send-link.svg?react'
import SendMessageIcon from '@/assets/icons/send-message.svg?react'
import UndoIcon from '@/assets/icons/undo.svg?react'
import Unlock from '@/assets/icons/unlock.svg?react'
import UpdateIcon from '@/assets/icons/update.svg?react'
import Button from '@/components/Button/Button'
import ConfirmModal from '@/components/ConfirmModal'
import { type Route } from '@/components/Tabs/Tabs'
import Tag from '@/components/Tag/Tag'
import Tooltip from '@/components/Tooltip/Tooltip'
import type { Action } from '@/components/common/BasicLayout/Actions'
import BasicLayout from '@/components/common/BasicLayout/BasicLayout'
import SendAvailabilityRequestModal from '@/components/common/SendAvailabilityRequestModal/SendAvailabilityRequestModal'
import { USER_PROFILE } from '@/constants/user-profile'
import { toast } from '@/hooks/useToast'
import useSendAvailabilityRequest from '@/mutations/useSendAvailabilityRequest'
import useSemesters from '@/queries/useSemesters'
import queryClient from '@/queryClient'
import useAuthStore from '@/store/useAuthStore'
import type { RouteWithHasPermission } from '@/types/route'
import { getUserStatusTag } from '@/utils/get-user-status-tag'
import { getUserStatusTooltip } from '@/utils/get-user-status-tooltip'

import styles from './UserView.module.scss'
import ArchiveUserModal from '../components/ArchiveUserModal'
import useScheduleArchiveUsers from '../hooks/useScheduleArchiveUsers'
import useActivateUser from '../mutations/useActivateUser'
import useBlockUser from '../mutations/useBlockUser'
import useSendActivationUserLink, {
  type ActivateLinkResponse
} from '../mutations/useSendUserActivationLink'
import useUser from '../queries/useUser'
import { getColorByProfile, getLabelByProfile } from '../utils/getProfileTag'

const routeApi = getRouteApi('/_auth/users/$userId')

const UserView = () => {
  const { t } = useTranslation(['users', 'common'])

  const { user: userStore } = useAuthStore()

  const { userId } = routeApi.useParams()

  const { data: user, refetch } = useUser(userId)
  const { data: semesters } = useSemesters()

  const [openActivationLinkModal, setOpenActivationLinkModal] = useState(false)
  const [openBlockConfirmModal, setOpenBlockConfirmModal] = useState(false)
  const [openActivateConfirmModal, setOpenActivateConfirmModal] =
    useState(false)
  const [openArchiveConfirmModal, setOpenArchiveConfirmModal] = useState(false)
  const [
    openSendAvailabilityRequestModal,
    setOpenSendAvailabilityRequestModal
  ] = useState(false)

  const handleBlock = () => {
    if (!!user) blockUser({ id: user.id })
  }

  const handleActivate = () => {
    if (!!user) activateUser({ id: user.id })
  }

  const handleArchive = () => {
    refetch()
    queryClient.invalidateQueries({
      queryKey: ['panelUsersAvailabilitySemestersList', { userId }]
    })
    setOpenArchiveConfirmModal(false)
  }

  const closeBlockConfirmModal = () => {
    refetch()
    setOpenBlockConfirmModal(false)
  }

  const { mutate: blockUser, isPending: isBlockUserLoading } = useBlockUser({
    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('toast.user-blocked', { NAME: user?.name })
      })
      closeBlockConfirmModal()
    },
    onError: () => {
      toast({
        variant: 'error',
        title: t('toast.user-blocked-failed', {
          NAME: user?.name
        })
      })
      closeBlockConfirmModal()
    }
  })

  const closeActivateConfirmModal = () => {
    refetch()
    setOpenActivateConfirmModal(false)
  }

  const { mutate: activateUser, isPending: isActivateUserLoading } =
    useActivateUser({
      onSuccess: () => {
        toast({
          variant: 'success',
          title: t('toast.user-activate', {
            NAME: user?.name
          })
        })
        closeActivateConfirmModal()
      },
      onError: () => {
        toast({
          variant: 'error',
          title: t('toast.user-activate', {
            NAME: user?.name
          })
        })
        closeActivateConfirmModal()
      }
    })

  const { isPending: isSendActivationLinkLoading, mutate: sendActivationLink } =
    useSendActivationUserLink({
      onSuccess: (data?: ActivateLinkResponse) => {
        if (data?.successfullySent) {
          if (user?.id) {
            toast({
              variant: 'success',
              title: t('toast.successfully-send-activation-link')
            })
          } else {
            toast({
              variant: 'success',
              title: t('toast.successfully-send-activation-links', {
                count: data?.successfullySent
              })
            })
          }
        }
        if (data?.unsuccessfullySent) {
          toast({
            variant: 'error',
            title: t('toast.failed-send-activation-links', {
              NUMBER: data.unsuccessfullySent
            })
          })
        }
        refetch()
        setOpenActivationLinkModal(false)
      }
    })

  const {
    mutate: sendAvailabilityRequest,
    isPending: isSendAvailabilityRequestPending
  } = useSendAvailabilityRequest({
    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('notification.successfully-sent-email')
      })
      setOpenSendAvailabilityRequestModal(false)
    },
    onError: () => {
      toast({
        variant: 'error',
        title: t('notification.failed-to-sent-email')
      })
    }
  })

  const { mutate: bulkScheduledArchiveUser } = useScheduleArchiveUsers({
    userName: user?.name,
    onSuccess: () => {
      refetch()
    }
  })

  const handleSendAvailabilityRequest = (semesterId: string) =>
    sendAvailabilityRequest({
      semesterId,
      userId
    })

  const handleSendActivationLink = () => {
    if (!user?.id) return
    const ids = [user.id]

    sendActivationLink({
      ids
    })
  }

  const routes: RouteWithHasPermission[] = [
    {
      title: t('navigation.details', { ns: 'common' }),
      link: {
        to: '/users/$userId/details'
      },
      hasPermission: true
    },
    {
      title: t('navigation.courses', { ns: 'common' }),
      link: {
        to: '/users/$userId/courses',
        search: {
          semester: semesters?.currentSemester?.id || semesters?.list[0].id
        }
      },
      hasPermission:
        (userStore?.id === userId || userStore?.isSuperAdmin) &&
        user?.profiles.includes(USER_PROFILE.TEACHER)
    }
  ]

  const routesWithPermission: Route[] = routes
    .filter(route => route.hasPermission)
    .map(route => ({
      title: route.title,
      link: route.link
    }))

  const isArchived = user?.status === 'archived'

  const actions =
    userStore?.isSuperAdmin && !isArchived
      ? ([
          {
            text: t('button.send-availability-request'),
            icon: <SendMessageIcon />,
            onClick: () => setOpenSendAvailabilityRequestModal(true),
            hidden: user?.status === 'blocked'
          },
          {
            icon: <UpdateIcon />,
            text: t('button.cancel-cover-lesson'),
            asLink: true,
            to: '/covered-and-cancelled-lessons/add-cover-cancel',
            search: {
              teacherId: userId
            },
            hidden: !user?.isTeacher || !userStore?.isSuperAdmin
          },
          {
            icon: <SendLink />,
            onClick: handleSendActivationLink,
            loading: isSendActivationLinkLoading,
            text:
              user?.status === 'active'
                ? t('button.resend-activation-link')
                : t('button.send-activation-link'),
            hidden: user?.status === 'blocked'
          },
          {
            icon: <Unlock />,
            onClick: () => {
              setOpenActivateConfirmModal(true)
            },
            loading: isActivateUserLoading,
            text: t('button.activate-user'),
            hidden: user?.status === 'active' || user?.status === 'inactive'
          },
          {
            icon: <Cancel />,
            onClick: () => {
              setOpenBlockConfirmModal(true)
            },
            loading: isBlockUserLoading,
            text: t('button.block-user'),
            variantAction: 'danger',
            hidden: user?.status === 'blocked' || user?.status === 'inactive'
          },
          {
            icon: <HistoryIcon />,
            onClick: () => {
              setOpenArchiveConfirmModal(true)
            },
            text: t('button.archive-user'),
            hidden: userStore?.id === userId
          },
          {
            icon: <UndoIcon />,
            onClick: () => {
              bulkScheduledArchiveUser({
                ids: [userId]
              })
            },
            text: t('button.undo-archiving'),
            hidden: !user?.scheduledArchiveDate
          }
        ] satisfies Action[])
      : []

  return (
    <BasicLayout
      routes={routesWithPermission}
      actions={actions}
      actionsAsDropdown
      moduleName={t('header.administration', { ns: 'common' })}
      headerVariant={isArchived ? 'gray' : 'default'}
      header={
        <div className={styles.headerWrapper}>
          <h1 className={styles.header}>{user?.name}</h1>
          <div className={styles.box}>
            {user?.status ? (
              <Tooltip
                trigger={
                  <Tag
                    {...getUserStatusTag(user.status)}
                    dataTestId="status-tag"
                    variant="on-blue"
                    disabled={isArchived}
                  />
                }
                text={getUserStatusTooltip(user.status)}
              />
            ) : null}

            <div className={styles.profiles}>
              <span className={styles.role}>{t('text.role')}</span>
              <div className={styles.profilesWrapper}>
                {!user?.profiles.length ? (
                  <Tag
                    variant="with-background"
                    label={t('text.user')}
                    color={getColorByProfile('User')}
                    disabled={isArchived}
                  />
                ) : (
                  user.profiles.map((userProfile, index) => (
                    <Tag
                      variant="with-background"
                      key={index}
                      label={getLabelByProfile(userProfile)}
                      color={getColorByProfile(userProfile)}
                      disabled={isArchived}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      }
      subheader={t('header.user-details')}
      breadcrumbs={[
        {
          title: t('common:navigation.teachers-and-users'),
          to: '/users'
        },
        { title: user?.name ?? '', to: '/users/$userId/details' }
      ]}
    >
      <Outlet />

      <ConfirmModal
        header={t('header.send-activation-link')}
        subheader={t('help.send-activation-link-single', {
          NAME: user?.name
        })}
        confirmButton={
          <Button
            variant="primary"
            onClick={handleSendActivationLink}
            loading={isSendActivationLinkLoading}
          >
            {t('button.send')}
          </Button>
        }
        open={openActivationLinkModal}
        onOpenChange={setOpenActivationLinkModal}
      />

      <ConfirmModal
        header={t('header.block-user')}
        subheader={t('help.want-to-block-user', {
          NAME: user?.name
        })}
        confirmButton={
          <Button
            variant="danger"
            onClick={handleBlock}
            loading={isBlockUserLoading}
          >
            {t('button.block')}
          </Button>
        }
        open={openBlockConfirmModal}
        onOpenChange={setOpenBlockConfirmModal}
      />

      <ConfirmModal
        header={t('header.activate-user')}
        subheader={t('help.want-to-activate-user', {
          NAME: user?.name
        })}
        confirmButton={
          <Button onClick={handleActivate} loading={isActivateUserLoading}>
            {t('button.activate')}
          </Button>
        }
        open={openActivateConfirmModal}
        onOpenChange={setOpenActivateConfirmModal}
      />

      {user ? (
        <ArchiveUserModal
          userName={user.name}
          open={openArchiveConfirmModal}
          onOpenChange={setOpenArchiveConfirmModal}
          ids={[userId]}
          onArchive={handleArchive}
        />
      ) : null}

      {user && semesters?.currentSemester?.id ? (
        <SendAvailabilityRequestModal
          open={openSendAvailabilityRequestModal}
          onOpenChange={setOpenSendAvailabilityRequestModal}
          selectedSemester={semesters?.currentSemester?.id}
          description={t('help.send-availability-request-to-one-user')}
          onSendRequest={handleSendAvailabilityRequest}
          sendRequestLoading={isSendAvailabilityRequestPending}
        />
      ) : null}
    </BasicLayout>
  )
}

export default UserView
